import type { SalaryStructureItem } from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import LabelWithRadio from '@/components/LabelWithRadio';
import { XIcon } from '@/icons';
import { snakeToCamel } from '@/utils/string.utils';
import { Button, Flex, Form, Input, Select, Space, Switch } from 'antd';
import type { Rule } from 'antd/es/form';
import type { FormInstance } from 'antd/es/form/Form';
import { useEffect } from 'react';
import { GRADE_LIST } from './salary-grade.types';

/** Where a grade's benefits live inside the grade form. */
const benefitsPath = (index: number) => [GRADE_LIST, index, 'benefits'];

type BenefitValue = {
  benefitName?: string;
  /** Flat amount or a percentage of one salary component. */
  type?: 'flat' | 'percentage';
  fixedAmount?: number | string | null;
  percentage?: number | string | null;
  percentageOf?: string | null;
  isTaxApplicable?: boolean;
  /**
   * True once the row has been saved from this drawer. Rows still false when
   * the drawer is closed without saving are dropped again.
   */
  status?: boolean;
};

type BenefitFormValues = {
  /** Scratch field for the "add a benefit" box; never part of the grade. */
  benefitName?: string;
  benefits: BenefitValue[];
};

type Props = CustomModalProps & {
  /** The grade form this drawer reads from and writes back into. */
  form: FormInstance;
  /** View mode: benefits can be read, but nothing can be changed. */
  readOnly?: boolean;
  data: {
    /** Index of the grade inside the grade list. */
    name: number;
    gradeName?: string;
    salaryStructures: SalaryStructureItem[];
  };
};

/**
 * Rules for the two money inputs. They only differ in wording and in the
 * ceiling a percentage has, so one factory covers both.
 */
const amountRules = (label: string, max?: number): Rule[] => [
  { required: true, message: `Please enter ${label}` },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      if (isNaN(Number(value))) {
        return Promise.reject(new Error('Enter numbers only'));
      }
      if (Number(value) < 0) {
        return Promise.reject(new Error('Enter positive numbers only'));
      }
      if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
        return Promise.reject(new Error('Max 2 decimal places'));
      }
      if (Number(value) < 1) {
        return Promise.reject(new Error(max ? 'Min 1%' : 'Min value is 1'));
      }
      if (max && Number(value) > max) {
        return Promise.reject(new Error(`Max ${max}%`));
      }
      return Promise.resolve();
    },
  },
];

/** One benefit: its name, how it is calculated, and whether tax applies. */
const BenefitRow = ({
  index,
  benefit,
  structures,
  readOnly,
  onRemove,
}: {
  index: number;
  benefit?: BenefitValue;
  structures: SalaryStructureItem[];
  readOnly: boolean;
  onRemove: () => void;
}) => {
  const form = Form.useFormInstance();
  const isPercentage = benefit?.type === 'percentage';

  return (
    <div className="border-b border-b-white/40 pb-5 last:border-b-0">
      <Form.Item name={[index, 'status']} initialValue={false} noStyle />

      <Flex
        justify="space-between"
        className="border-b border-b-theme-gray-800 pb-4 border-dashed mb-2"
      >
        <Form.Item name={[index, 'benefitName']} noStyle>
          <Input
            disabled
            placeholder="Enter benefit name"
            className="!p-0 !border-0 !h-5 disabled:!cursor-auto !bg-transparent"
          />
        </Form.Item>

        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="cursor-pointer border border-theme-primary text-theme-primary size-6 rounded-md flex justify-center items-center"
          >
            <XIcon size={16} />
          </button>
        )}
      </Flex>

      <Flex align="center" justify="space-between" gap={24}>
        <div className="w-1/2">
          <LabelWithRadio
            initialValue="flat"
            label="Benefits Setup"
            name={[index, 'type']}
            // The two setups keep different fields, so the unused ones clear.
            onRadioChange={() =>
              form.resetFields([
                ['benefits', index, 'fixedAmount'],
                ['benefits', index, 'percentageOf'],
                ['benefits', index, 'percentage'],
              ])
            }
            options={[
              { label: 'Flat', value: 'flat' },
              { label: '%', value: 'percentage' },
            ]}
          />

          {isPercentage ? (
            <Space.Compact className="compact-select w-full items-center border border-theme-slate-700 rounded-md pr-1">
              <Form.Item
                noStyle
                name={[index, 'percentage']}
                rules={amountRules('percentage', 100)}
              >
                <Input
                  suffix="%"
                  placeholder="00"
                  autoComplete="off"
                  className="!border-0 !bg-transparent w-full"
                />
              </Form.Item>

              <Form.Item
                noStyle
                name={[index, 'percentageOf']}
                rules={[{ required: true, message: 'Please select component' }]}
              >
                <Select
                  size="small"
                  className="!w-[160px]"
                  placement="bottomRight"
                  placeholder="Select component"
                  options={structures.map((structure) => ({
                    value: structure.component_name,
                    label: snakeToCamel(structure.component_name),
                  }))}
                />
              </Form.Item>
            </Space.Compact>
          ) : (
            <Form.Item
              name={[index, 'fixedAmount']}
              rules={amountRules('amount')}
            >
              <Input prefix="BDT" placeholder="00" autoComplete="off" />
            </Form.Item>
          )}
        </div>

        <Form.Item
          className="!mb-0"
          layout="horizontal"
          initialValue={false}
          label="Applicable For Tax"
          name={[index, 'isTaxApplicable']}
        >
          <Switch className="custom-switch" />
        </Form.Item>
      </Flex>
    </div>
  );
};

/**
 * Benefits of a single grade.
 *
 * The drawer edits a copy in its own form and only writes back into the grade
 * form, never to the server: the grade panel sends everything on Save.
 */
const ManageBenefit = ({
  open,
  handleClose,
  data,
  form,
  readOnly = false,
}: Props) => {
  const [childForm] = Form.useForm<BenefitFormValues>();
  const { name, gradeName, salaryStructures = [] } = data || {};
  const benefits = Form.useWatch('benefits', childForm);

  // Opening the drawer takes a fresh copy of the grade's benefits.
  useEffect(() => {
    if (open) {
      childForm.setFieldsValue({
        benefits: form.getFieldValue(benefitsPath(name)) || [],
      });
    }
  }, [open, name, form, childForm]);

  /** Save: every row in the drawer becomes part of the grade. */
  const handleSubmit = ({ benefits: edited = [] }: BenefitFormValues) => {
    form.setFieldValue(
      benefitsPath(name),
      edited.map((benefit) => ({ ...benefit, status: true })),
    );
    handleClose();
  };

  /** Close without saving: rows added in this session are dropped again. */
  const handleDismiss = () => {
    if (!readOnly) {
      const current: BenefitValue[] = childForm.getFieldValue('benefits') || [];
      form.setFieldValue(
        benefitsPath(name),
        current.filter((benefit) => benefit.status),
      );
    }
    handleClose();
  };

  /** Adds an empty benefit under the name typed above the list. */
  const handleAdd = () => {
    const benefitName = String(
      childForm.getFieldValue('benefitName') || '',
    ).trim();

    if (!benefitName) {
      childForm.setFields([
        { name: 'benefitName', errors: ['Please enter name'] },
      ]);
      return;
    }

    const current: BenefitValue[] = childForm.getFieldValue('benefits') || [];
    childForm.setFieldValue('benefits', [...current, { benefitName }]);
    childForm.resetFields(['benefitName']);
  };

  return (
    <CustomDrawer
      width={800}
      open={open}
      onClose={handleDismiss}
      title={`${gradeName || 'Grade'} benefits`}
    >
      <Form
        size="large"
        form={childForm}
        disabled={readOnly}
        layout="vertical"
        className="custom-form space-y-6"
        onFinish={handleSubmit}
      >
        {/*
          Outside the list on purpose: a `Form.Item` rendered inside
          `Form.List` is addressed relative to the list, so this scratch field
          would end up at `benefits.benefitName` and never be readable.
        */}
        {!readOnly && (
          <Flex align="end" gap={16}>
            <Form.Item
              name="benefitName"
              label="Benefit name"
              className="disabled-bg-slate-900 flex-1 !mb-0"
            >
              <Input placeholder="Mobile bill" onPressEnter={handleAdd} />
            </Form.Item>
            <Button
              htmlType="button"
              onClick={handleAdd}
              className="!bg-transparent border border-theme-primary text-theme-primary h-11"
            >
              Add
            </Button>
          </Flex>
        )}

        <Form.List name="benefits">
          {(fields, { remove }) => (
            <>
              <h4 className="text-xl text-white font-semibold">
                Benefits ({fields.length})
              </h4>

              <div className="space-y-4 mt-6 p-4 border border-theme-gray-800 rounded-lg">
                {!fields.length && (
                  <p className="text-sm text-white/45 text-center py-4">
                    No benefits added for this grade.
                  </p>
                )}

                {fields.map(({ key, name: index }) => (
                  <BenefitRow
                    key={key}
                    index={index}
                    readOnly={readOnly}
                    benefit={benefits?.[index]}
                    structures={salaryStructures}
                    onRemove={() => remove(index)}
                  />
                ))}
              </div>

              {!readOnly && (
                <Flex justify="end" className="mt-6">
                  <Button htmlType="submit" className="btn-primary h-11 min-w-20">
                    Save
                  </Button>
                </Flex>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </CustomDrawer>
  );
};

export default ManageBenefit;
